import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { TimePicker } from '@/components/ui/time-picker';
import { Building, Clock, Beaker, Package, ArrowRight, Plus, Trash2 } from 'lucide-react';
import { useSitePrevisionnel, type BuildingData } from '@/hooks/useSitePrevisionnel';
import { useCommandeFormules, type FormuleBeton } from '@/hooks/useCommandeFormules';
import { useCommandeGlobaleOptions } from '@/hooks/useCommandeGlobaleOptions';

export interface ParsedOuvrage {
  name: string;
  building?: string;
  level?: string;
  volume: number;
}

interface ParsedFormula {
  name: string;
  strength?: string;
  exposure_class?: string;
  consistency?: string;
}

interface ParsedOrder {
  workItems: ParsedOuvrage[];
  formula?: ParsedFormula;
  options?: string[];
  heure?: string;
  date?: string;
  comment?: string;
  confidence: number;
}

export interface CompletedWorkItem {
  id: string;
  workItemId: string;
  building: string;
  level: string;
  name: string;
  volume: number;
  formulaId: string;
  desiredTime: string;
}

export interface VoiceOrderCompleteData {
  workItems: CompletedWorkItem[];
  date: string;
  options: string[];
  comment?: string;
}

interface VoiceOrderQuestionnaireProps {
  parsedOrder: ParsedOrder;
  siteId: string;
  onComplete: (data: VoiceOrderCompleteData) => void;
  onBack: () => void;
}

export const VoiceOrderQuestionnaire: React.FC<VoiceOrderQuestionnaireProps> = ({
  parsedOrder,
  siteId,
  onComplete,
  onBack,
}) => {
  const { buildings } = useSitePrevisionnel(siteId);
  const { formules } = useCommandeFormules(siteId);
  const { options: globalOptions } = useCommandeGlobaleOptions(siteId);

  // State for each work item completion
  const [completedWorkItems, setCompletedWorkItems] = useState<CompletedWorkItem[]>([]);
  
  // Default values from parsed order
  const [defaultFormula, setDefaultFormula] = useState<string>('');
  const [defaultHeure, setDefaultHeure] = useState<string>(parsedOrder.heure || '08:00');
  const [selectedOptions, setSelectedOptions] = useState<string[]>(parsedOrder.options || []);
  const [deliveryDate, setDeliveryDate] = useState<string>(
    parsedOrder.date || new Date().toISOString().split('T')[0]
  );

  // Get available levels for a building - only those with actual work items
  const getLevelsForBuilding = (buildingName: string): string[] => {
    const building = buildings.find(b => b.name === buildingName);
    if (!building) return [];
    // Filter levels that have at least one work item with volume > 0
    return building.levels.filter(niveau =>
      building.customWorkItems.some(o => (building.volumes[niveau]?.[o.id] || 0) > 0)
    );
  };

  // Find matching work item from prévisionnel
  const findMatchingWorkItem = (parsedName: string) => {
    const normalizedParsed = parsedName.toLowerCase().trim();

    for (const building of buildings) {
      for (const ouvrage of building.customWorkItems) {
        const normalizedLabel = ouvrage.name.toLowerCase().trim();
        // Check for fuzzy match
        if (
          normalizedLabel.includes(normalizedParsed) ||
          normalizedParsed.includes(normalizedLabel) ||
          // Handle plural/singular forms
          normalizedLabel.replace(/s$/, '') === normalizedParsed.replace(/s$/, '')
        ) {
          // Find the level with this work item
          const niveau = building.levels.find(n => (building.volumes[n]?.[ouvrage.id] || 0) > 0);
          if (niveau) {
            return {
              id: ouvrage.id,
              name: ouvrage.name,
              building: building.name,
              level: niveau,
            };
          }
        }
      }
    }
    return null;
  };

  // Initialize completed work items from parsed order
  useEffect(() => {
    if (parsedOrder.workItems && parsedOrder.workItems.length > 0 && buildings.length > 0) {
      // Auto-detect building and level if only one available
      const singleBuilding = buildings.length === 1 ? buildings[0].name : '';
      const availableLevels = singleBuilding ? getLevelsForBuilding(singleBuilding) : [];
      const singleLevel = availableLevels.length === 1 ? availableLevels[0] : '';

      const initial = parsedOrder.workItems.map((o, idx) => {
        // Try to match with prévisionnel
        const matched = findMatchingWorkItem(o.name);

        return {
          id: `voice-${idx}-${Date.now()}`,
          workItemId: matched?.id || o.name,
          building: o.building || matched?.building || singleBuilding,
          level: o.level || matched?.level || singleLevel,
          name: matched?.name || o.name,
          volume: o.volume,
          formulaId: '',
          desiredTime: parsedOrder.heure || '08:00',
        };
      });
      setCompletedWorkItems(initial);
    }
  }, [parsedOrder, buildings]);

  // Try to match parsed formula to available formulas
  useEffect(() => {
    if (parsedOrder.formula && formules.length > 0) {
      const matched = formules.find(f => {
        const nameMatch = f.name.toLowerCase().includes(parsedOrder.formula!.name.toLowerCase());
        const strengthMatch = parsedOrder.formula!.strength 
          ? f.resistance?.toLowerCase().includes(parsedOrder.formula!.strength.toLowerCase())
          : true;
        return nameMatch || strengthMatch;
      });
      if (matched) {
        setDefaultFormula(matched.id);
      }
    }
  }, [parsedOrder.formula, formules]);

  // Match parsed options with available options (fuzzy match for PUMI, etc.)
  useEffect(() => {
    if (parsedOrder.options && parsedOrder.options.length > 0 && globalOptions.length > 0) {
      const matchedOptions = parsedOrder.options
        .map(parsedOpt => {
          const normalizedParsed = parsedOpt.toLowerCase().trim();
          const match = globalOptions.find(opt => {
            const normalizedOpt = opt.name.toLowerCase();
            // Match if option name contains parsed option or vice versa
            return normalizedOpt.includes(normalizedParsed) ||
                   normalizedParsed.includes(normalizedOpt.split(' ')[0].toLowerCase());
          });
          return match?.name;
        })
        .filter(Boolean) as string[];
      
      if (matchedOptions.length > 0) {
        setSelectedOptions(matchedOptions);
      }
    }
  }, [parsedOrder.options, globalOptions]);

  // Apply default formula to all work items when detected
  useEffect(() => {
    if (defaultFormula && completedWorkItems.length > 0) {
      const needsUpdate = completedWorkItems.some(o => !o.formulaId);
      if (needsUpdate) {
        setCompletedWorkItems(prev => prev.map(o => ({
          ...o,
          formulaId: o.formulaId || defaultFormula,
        })));
      }
    }
  }, [defaultFormula]);

  // Update work item field
  const updateWorkItem = (id: string, field: keyof CompletedWorkItem, value: string | number) => {
    setCompletedWorkItems(prev =>
      prev.map(o => o.id === id ? { ...o, [field]: value } : o)
    );
  };

  // Get available work items for building/level
  const getWorkItemsForBuildingLevel = (buildingName: string, level: string) => {
    const building = buildings.find(b => b.name === buildingName);
    if (!building) return [];
    return building.customWorkItems.filter(o => {
      const vol = building.volumes[level]?.[o.id] || 0;
      return vol > 0;
    });
  };

  // Check if all work items are complete - each work item must have its own formula
  const isComplete = completedWorkItems.length > 0 && completedWorkItems.every(o =>
    o.building && o.level && o.name && o.volume > 0 && o.formulaId
  );

  // Remove work item
  const removeWorkItem = (id: string) => {
    setCompletedWorkItems(prev => prev.filter(o => o.id !== id));
  };

  // Available options for selection (PUMI, pompe, etc.)
  const deliveryOptions = globalOptions.filter(o => 
    o.category === 'options_concrete_pour' && o.active
  );

  const handleSubmit = () => {
    if (!isComplete) return;

    // Apply default formula if not set
    const finalWorkItems = completedWorkItems.map(o => ({
      ...o,
      formulaId: o.formulaId || defaultFormula,
      desiredTime: o.desiredTime || defaultHeure,
    }));

    onComplete({
      workItems: finalWorkItems,
      date: deliveryDate,
      options: selectedOptions,
      comment: parsedOrder.comment,
    });
  };

  return (
    <div className="space-y-xl w-full max-w-lg">
      <div className="text-center mb-lg">
        <h3 className="text-title">Compléter la commande</h3>
        <p className="text-body-md text-muted-foreground">
          Vérifiez et complétez les informations manquantes
        </p>
      </div>

      {/* Date de livraison */}
      <Card>
        <CardHeader>
          <CardTitle className="text-label flex items-center gap-sm">
            <Clock className="w-4 h-4" />
            Date et heure
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-md">
          <div className="grid grid-cols-2 gap-lg">
            <div className="space-y-1">
              <Label className="text-body-sm">Date de livraison</Label>
              <input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                className="w-full h-9 px-md rounded-md border border-input bg-background text-body-md"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-body-sm">Heure par défaut</Label>
              <TimePicker value={defaultHeure} onChange={setDefaultHeure} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Formule par défaut détectée - information uniquement */}
      {defaultFormula && (
        <Card>
          <CardHeader>
            <CardTitle className="text-label flex items-center gap-sm">
              <Beaker className="w-4 h-4" />
              Formule détectée
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(() => {
              const formula = formules.find(f => f.id === defaultFormula);
              return formula ? (
                <div className="flex items-center gap-sm flex-wrap">
                  <span className="font-medium">{formula.name}</span>
                  {formula.resistance && <Badge variant="outline" className="text-body-sm">{formula.resistance}</Badge>}
                  {formula.consistance && <Badge variant="outline" className="text-body-sm">{formula.consistance}</Badge>}
                </div>
              ) : null;
            })()}
            <p className="text-helper text-muted-foreground mt-2">
              Cette formule sera appliquée par défaut. Vous pouvez modifier par ouvrage ci-dessous.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Ouvrages */}
      <Card>
        <CardHeader>
          <CardTitle className="text-label flex items-center gap-sm">
            <Building className="w-4 h-4" />
            Ouvrages à bétonner ({completedWorkItems.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-lg">
          {completedWorkItems.map((workItem, idx) => (
            <div key={workItem.id} className="p-md bg-muted/50 rounded-lg space-y-md">
              <div className="flex items-center justify-between">
                <Badge variant="secondary">Ouvrage {idx + 1}</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeWorkItem(workItem.id)}
                  className="h-8 w-8 p-0 text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>

              {/* Désignation et volume */}
              <div className="grid grid-cols-2 gap-sm">
                <div className="space-y-1">
                  <Label className="text-body-sm">Désignation</Label>
                  <p className="text-body-md">{workItem.name}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-body-sm">Volume</Label>
                  <p className="text-body-md">{workItem.volume} m³</p>
                </div>
              </div>

              {/* Bâtiment et Niveau */}
              <div className="grid grid-cols-2 gap-sm">
                <div className="space-y-1">
                  <Label className="text-body-sm">Bâtiment *</Label>
                  <Select
                    value={workItem.building}
                    onValueChange={(v) => {
                      updateWorkItem(workItem.id, 'building', v);
                      updateWorkItem(workItem.id, 'level', ''); // Reset level
                    }}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Bât..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Bâtiment</SelectLabel>
                        {buildings.map(b => (
                          <SelectItem key={b.name} value={b.name}>{b.name}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-body-sm">Niveau *</Label>
                  <Select
                    value={workItem.level}
                    onValueChange={(v) => updateWorkItem(workItem.id, 'level', v)}
                    disabled={!workItem.building}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="N..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectGroup>
                        <SelectLabel>Niveau</SelectLabel>
                        {getLevelsForBuilding(workItem.building).map(n => (
                          <SelectItem key={n} value={n}>{n}</SelectItem>
                        ))}
                      </SelectGroup>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Formule par ouvrage - toujours visible */}
              <div className="space-y-1">
                <Label className="text-body-sm">Formule *</Label>
                <Select
                  value={workItem.formulaId}
                  onValueChange={(v) => updateWorkItem(workItem.id, 'formulaId', v)}
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="Sélectionner une formule" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Formule</SelectLabel>
                      {formules.map(f => (
                        <SelectItem key={f.id} value={f.id}>
                          <div className="flex items-center gap-sm">
                            <span>{f.name}</span>
                            {f.resistance && <Badge variant="outline" className="text-body-sm">{f.resistance}</Badge>}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ))}

          {completedWorkItems.length === 0 && (
            <p className="text-body-md text-muted-foreground text-center py-lg">
              Aucun ouvrage détecté
            </p>
          )}
        </CardContent>
      </Card>

      {/* Options de livraison */}
      {deliveryOptions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-label flex items-center gap-sm">
              <Package className="w-4 h-4" />
              Options de livraison
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-sm">
              {deliveryOptions.map(opt => (
                <div key={opt.id} className="flex items-center gap-sm">
                  <Checkbox
                    id={opt.id}
                    checked={selectedOptions.includes(opt.name)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedOptions([...selectedOptions, opt.name]);
                      } else {
                        setSelectedOptions(selectedOptions.filter(o => o !== opt.name));
                      }
                    }}
                  />
                  <Label htmlFor={opt.id} className="text-body-md cursor-pointer">
                    {opt.name}
                  </Label>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="flex gap-md">
        <Button variant="outline" onClick={onBack} className="flex-1">
          Retour
        </Button>
        <Button 
          onClick={handleSubmit} 
          disabled={!isComplete}
          className="flex-1 gap-sm"
        >
          Continuer
          <ArrowRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
